/**
 * Results Page
 * ============
 * Case studies, testimonials, and performance metrics.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Star } from 'lucide-react';
import { Button } from '../../components/shared/Button';
import { StatCard, TestimonialCard } from '../../components/shared/Card';
import { SectionHeader } from '../../components/shared/SectionBadge';
import { FadeReveal, StaggerReveal } from '../../components/animations/FadeReveal';
import { RESULTS_STATS } from '../../utils/constants';

/**
 * Results Page Component
 * Showcases results and testimonials.
 * 
 * @returns {JSX.Element} Results page
 */
function Results() {
  const testimonials = [
    {
      quote: "DME-CPH transformed our enrollment strategy. We saw a 35% increase in qualified applications within the first semester.",
      author: "Dr. Sarah Johnson",
      role: "VP of Enrollment",
      company: "State University",
      rating: 5,
    },
    {
      quote: "The predictive analytics are incredibly accurate. We can now identify at-risk students before they even consider leaving.",
      author: "Michael Chen",
      role: "Director of Admissions",
      company: "Tech College",
      rating: 5,
    },
    {
      quote: "Best investment we've made for our enrollment team. The ROI was evident within the first quarter.",
      author: "Emily Rodriguez",
      role: "Dean of Students",
      company: "Liberal Arts College",
      rating: 5,
    },
  ];
  
  const caseStudies = [
    {
      title: "State University Increases Enrollment by 28%",
      metric: "+28%",
      metricLabel: "Enrollment Growth",
      description: "Using DME-CPH's predictive analytics, State University optimized their marketing spend and improved targeting.",
    },
    {
      title: "Private College Reduces Cost Per Enrollment by 42%",
      metric: "-42%",
      metricLabel: "Cost Reduction",
      description: "By leveraging AI-powered insights, the college identified the most effective channels and campaigns.",
    },
    {
      title: "Community College Improves Retention to 91%",
      metric: "91%",
      metricLabel: "Retention Rate",
      description: "Early warning systems helped advisors intervene with at-risk students before it was too late.",
    },
  ];
  
  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-dme-blue via-dme-blue-600 to-dme-blue-800 py-24 lg:py-32">
        <div className="container-dme relative z-10">
          <FadeReveal>
            <div className="max-w-3xl mx-auto text-center">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-medium mb-6">
                <TrendingUp className="w-4 h-4 text-dme-orange" />
                Proven Results
              </span>
              <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
                Real Results from Real Partners
              </h1>
              <p className="text-lg lg:text-xl text-white/80 max-w-2xl mx-auto">
                See how institutions like yours are achieving remarkable 
                outcomes with DME-CPH.
              </p>
            </div>
          </FadeReveal>
        </div>
      </section>
      
      {/* Stats Section */}
      <section className="section bg-white">
        <div className="container-dme">
          <FadeReveal>
            <SectionHeader
              badge="Impact Metrics"
              title="Numbers That Speak"
              subtitle="Average results achieved by our partner institutions."
              align="center"
              className="mb-16"
            />
          </FadeReveal>
          
          <StaggerReveal 
            selector=".stat-card"
            stagger={0.15}
            className="grid grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {RESULTS_STATS.map((stat, index) => (
              <div key={index} className="stat-card">
                <StatCard
                  value={stat.value}
                  suffix={stat.suffix}
                  label={stat.label}
                  description={stat.description}
                />
              </div>
            ))}
          </StaggerReveal>
        </div>
      </section>
      
      {/* Case Studies */}
      <section className="section bg-dme-gray-50">
        <div className="container-dme">
          <FadeReveal>
            <SectionHeader
              badge="Case Studies"
              title="Success Stories"
              subtitle="Learn how our partners achieved their goals."
              align="center"
              className="mb-16"
            />
          </FadeReveal>
          
          <StaggerReveal 
            selector=".case-study"
            stagger={0.1}
            className="grid grid-cols-1 lg:grid-cols-3 gap-8"
          >
            {caseStudies.map((study, index) => (
              <div key={index} className="case-study bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="mb-6">
                  <span className="text-5xl font-heading font-extrabold text-dme-orange">
                    {study.metric}
                  </span>
                  <p className="text-sm text-dme-gray-500 mt-1">{study.metricLabel}</p>
                </div>
                <h3 className="font-heading font-bold text-xl text-dme-blue-800 mb-3">
                  {study.title}
                </h3>
                <p className="text-dme-gray-600">
                  {study.description}
                </p>
              </div>
            ))}
          </StaggerReveal>
        </div>
      </section>
      
      {/* Testimonials */}
      <section className="section bg-white">
        <div className="container-dme">
          <FadeReveal>
            <SectionHeader
              badge="Testimonials"
              title="What Our Partners Say"
              subtitle="Hear from the people who use DME-CPH every day."
              align="center"
              className="mb-16"
            />
          </FadeReveal>
          
          <StaggerReveal 
            selector=".testimonial-card"
            stagger={0.1}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <TestimonialCard {...testimonial} />
              </div>
            ))}
          </StaggerReveal>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="section bg-dme-blue-800">
        <div className="container-dme">
          <FadeReveal>
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading font-bold text-3xl lg:text-4xl text-white mb-4">
                Join Our Success Stories
              </h2>
              <p className="text-lg text-white/70 mb-8">
                Start your journey to better enrollment outcomes today.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button variant="primary" size="lg" rightIcon={<ArrowRight className="w-5 h-5" />}>
                    Get Started
                  </Button>
                </Link>
                <Link to="/demo">
                  <Button variant="outline-white" size="lg">
                    View Demo
                  </Button>
                </Link>
              </div>
            </div>
          </FadeReveal>
        </div>
      </section>
    </div>
  );
}

export default Results;
